/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import {
    DEFAULT_LOCALE,
    KAVERI_GLOBAL_CATCH_NAME,
    KAVERI_GLOBAL_CATCH_RETRY_LIMIT,
} from '../../config';
import { DeviceModel } from '../../core/device';
import { DimensionScaler } from '../../core/device/DimensionScaler';
import { ErrorTypes } from '../../core/error';
import { Locale } from '../../core/locale/Locale';
import { RestartReasonsOperationsLogger } from '../../core/logging';
import { Log } from '../../core/logging/Log';
import { LocationUrls } from '../../core/navigation/LocationUrls';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { CcbSwitch, CcbSwitchImpl } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { appStart } from '../../store/appl/appl.reducers';
import { sos } from '../../store/error/erroroverlays.reducers';
import {
    clearSystemHealthStatsInterval,
    logSystemHealthStats,
} from '../../store/healthstats/systemhealthstats.reducers';
import { setLocationUrl } from '../../store/urlchangeevents/urlchangeevents.reducers';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { CollectionScreen } from '../CollectionScreen';
import { DebugScreen } from '../DebugOverlay';
import { ErrorScreen } from '../ErrorScreen';
import {
    CursorEvents,
    KeyboardEvents,
    MouseEvents,
    NetworkChangeEvents,
    SystemInfoEvent,
    URLChangeEvents,
    VisibilityEvents,
} from '../Events';
import { FullScreenPlayback } from '../FullScreenPlayback';
import { Guide } from '../Guide';
import { EmptyGuide } from '../Guide/EmptyGuide';
import { useGlobalCatch } from '../hooks';
import { MyShowsScreen } from '../MyShows';
import { OverlayDialog } from '../OverlayDialog';
import { ProgramScreen } from '../ProgramScreen';
import { SettingsRoutes } from '../SettingsScreen/SettingsRoutes';
import { SignInScreen } from '../SignInScreen';
import { SplashScreen } from '../SplashScreen';
import { VideoPlayer } from '../VideoPlayer';

import './App.scss';

const App: FunctionComponent = (): JSX.Element => {
    const TAG = 'App';

    const dispatch = useTivoDispatch();

    // TODO: Need to remove from redux store.
    // const { isApplicationReady } = useSelector((state: RootState) => state.appl);

    const [isApplicationReady, setIsApplicationReady] = useState(false);
    const { visibilityState } = useTivoSelector((state: RootState) => state.visibilityEvents);

    const globalCatches = useGlobalCatch({
        name: KAVERI_GLOBAL_CATCH_NAME,
        onCatch: DeviceModel.restartApplication,
        retryLimit: KAVERI_GLOBAL_CATCH_RETRY_LIMIT,
    });

    const initCcbSwitch = () => {
        const oCcbSwitchImpl = new CcbSwitchImpl();
        oCcbSwitchImpl
            .initialize()
            .then(() => {
                CcbSwitch.setCcbSwitchImpl(oCcbSwitchImpl);
                if (CcbSwitch.getInstance().isEnabled(CcbSwitchName.ENABLE_DEVELOPER_SANDBOX)) {
                    Log.setLogLevel(Log.LogLevel.INFO);
                }
            })
            .catch((error) => {
                Log.e(TAG, 'Failed to initalize CcbSwitch', error);
            });
    };

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_PAUSE) {
            RestartReasonsOperationsLogger.getInstance().updateAppState(
                RestartReasonsOperationsLogger.AppState.BACKGROUND,
            );
        } else if (visibilityState === VisibilityState.ON_RESUME) {
            RestartReasonsOperationsLogger.getInstance().updateAppState(
                RestartReasonsOperationsLogger.AppState.RUNNING,
            );
        }
    }, [visibilityState]);

    useEffect(() => {
        initCcbSwitch();
        try {
            DimensionScaler.initialize(window.innerWidth, window.innerHeight);
        } catch (error) {
            Log.e(TAG, 'Failed to initialize DimensionScaler', error);
        }
        Locale.setLocale(DEFAULT_LOCALE);
        DeviceModel.initialize()
            .then(() => {
                setIsApplicationReady(true);
                Log.i(TAG, 'DeviceModel initialized successfully');
            })
            .catch(() => {
                Log.w(
                    TAG,
                    'Failed to initialize DeviceModel. Starting application with default properties.',
                );
            })
            .finally(() => {
                const isValidConfig = DeviceModel.getTiVoSettings().isValidMsoConfig();
                if (!isValidConfig) {
                    dispatch(sos(ErrorTypes.SOS_MESSAGE_CODE_H113));
                } else if (DeviceModel.isValidDevice()) {
                    if (globalCatches.isRetriesDone()) {
                        dispatch(sos(ErrorTypes.SOS_MESSAGE_CODE_H111));
                    } else {
                        dispatch(appStart());
                    }
                } else {
                    window.open('./404.html', '_self');
                }
            });
        return () => {
            dispatch(logSystemHealthStats());
            dispatch(clearSystemHealthStatsInterval());
        };
    }, []);

    useEffect(() => {
        if (!isApplicationReady) dispatch(setLocationUrl(LocationUrls.SPLASH_SCREEN));
    }, [isApplicationReady]);

    return (
        <React.Fragment>
            <NetworkChangeEvents />
            <MouseEvents />
            <CursorEvents />
            <SystemInfoEvent />
            <KeyboardEvents>
                {/* TODO: need to remove the isApplicationReady check from here */}
                {isApplicationReady ? (
                    <React.Fragment>
                        <VideoPlayer />
                        <DebugScreen />
                        <BrowserRouter basename={DeviceModel.getAppHomePage()}>
                            <URLChangeEvents />
                            <VisibilityEvents />
                            {/* TODO: replace browserRoutes with createBrowserRouter */}
                            <Routes>
                                <Route path="/" element={<SplashScreen />} />
                                <Route path="error" element={<ErrorScreen />} />
                                <Route path="guide" element={<Guide />} />
                                <Route path="guide/empty" element={<EmptyGuide />} />
                                <Route path="main-settings/*" element={<SettingsRoutes />} />
                                <Route path="signin" element={<SignInScreen />} />
                                <Route path="video-player" element={<FullScreenPlayback />} />
                                <Route path="myshows" element={<MyShowsScreen />} />
                                <Route path="collection" element={<CollectionScreen />} />
                                <Route
                                    path={`${LocationUrls.PROGRAM_SCREEN}/*`}
                                    element={<ProgramScreen />}
                                />
                            </Routes>
                        </BrowserRouter>
                    </React.Fragment>
                ) : null}
            </KeyboardEvents>
            <OverlayDialog />
        </React.Fragment>
    );
};

export default App;
